<script setup>
import { computed, ref, watch } from 'vue';
import { marked } from 'marked';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PlatformBadge from './PlatformBadge.vue';
import { exportReportPdf } from '../utils/pdfExport.js';

const props = defineProps({
  report: { type: Object, default: null },
  open: { type: Boolean, default: false },
  profile: { type: Object, default: null },
  context: { type: Object, default: () => ({}) }, // { summary, sentiment, topics, insights, competitors, period, platform, publicReaction, topMentioners, engagement }
});
const emit = defineEmits(['close']);

marked.setOptions({ gfm: true, breaks: true });

const renderedHtml = computed(() => {
  if (!props.report?.content) return '';
  return marked.parse(props.report.content);
});

const isExporting = ref(false);

const createdAtFormatted = computed(() => {
  if (!props.report?.createdAt) return '';
  return new Date(props.report.createdAt).toLocaleString('pt-BR');
});

const sentimentPct = computed(() => {
  const s = props.context?.summary?.sentiment || {};
  const total = (s.positive || 0) + (s.negative || 0) + (s.neutral || 0);
  if (!total) return { positive: 0, negative: 0, neutral: 0 };
  return {
    positive: Math.round((s.positive / total) * 100),
    negative: Math.round((s.negative / total) * 100),
    neutral: Math.round((s.neutral / total) * 100),
  };
});

async function exportPdf() {
  if (!props.report) return;
  isExporting.value = true;
  try {
    await exportReportPdf({
      report: props.report,
      profile: props.profile,
      context: props.context,
    });
  } finally {
    isExporting.value = false;
  }
}

function downloadMarkdown() {
  if (!props.report) return;
  const blob = new Blob([props.report.content], { type: 'text/markdown' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${(props.report.title || 'relatorio').replace(/\s+/g, '_')}.md`;
  a.click();
}

watch(
  () => props.open,
  isOpen => {
    if (!isOpen) return;
    const handler = e => {
      if (e.key === 'Escape') emit('close');
    };
    document.addEventListener('keydown', handler, { once: true });
  }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="report-modal-root fixed inset-0 z-50 flex items-stretch justify-center bg-black/70 backdrop-blur-sm"
      @click.self="emit('close')"
    >
      <div
        class="report-modal-container my-4 flex w-full max-w-5xl flex-col overflow-hidden rounded-xl shadow-2xl"
      >
        <!-- Header (keeps gradient/dark for contrast) -->
        <header
          class="flex items-start justify-between gap-4 bg-gradient-to-r from-blue-600 to-indigo-600 p-5 text-white"
        >
          <div class="min-w-0 flex-1">
            <p
              class="mb-1 text-[10px] font-bold uppercase tracking-widest opacity-80"
            >
              Relatório Estratégico
            </p>
            <h2 class="truncate text-xl font-bold">
              {{ report?.title || 'Relatório' }}
            </h2>
            <div
              class="mt-2 flex flex-wrap items-center gap-3 text-xs opacity-90"
            >
              <span v-if="profile" class="flex items-center gap-1.5">
                <span class="i-lucide-user size-3.5" />
                {{ profile.name }}
              </span>
              <PlatformBadge
                v-if="profile?.platform"
                :platform="profile.platform"
                size="xs"
              />
              <span v-if="context.period" class="flex items-center gap-1.5">
                <span class="i-lucide-calendar-days size-3.5" />
                Últimos {{ context.period }} dias
              </span>
              <span v-if="createdAtFormatted" class="flex items-center gap-1.5">
                <span class="i-lucide-clock size-3.5" />
                {{ createdAtFormatted }}
              </span>
            </div>
          </div>
          <div class="flex shrink-0 gap-2">
            <Button
              variant="solid"
              color="slate"
              size="sm"
              icon="i-lucide-file-down"
              :is-loading="isExporting"
              class="!bg-white/15 !text-white hover:!bg-white/25"
              @click="exportPdf"
            >
              Exportar PDF
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon="i-lucide-download"
              class="!text-white hover:!bg-white/15"
              title="Baixar Markdown"
              @click="downloadMarkdown"
            />
            <Button
              variant="ghost"
              size="sm"
              icon="i-lucide-x"
              class="!text-white hover:!bg-white/15"
              @click="emit('close')"
            />
          </div>
        </header>

        <!-- Body (forced light theme — document style) -->
        <div class="report-body flex-1 overflow-y-auto">
          <div v-if="!report" class="flex items-center justify-center p-10">
            <Spinner />
          </div>

          <div v-else class="mx-auto max-w-4xl space-y-6 p-8">
            <!-- Summary KPIs -->
            <section
              v-if="context.summary"
              class="grid grid-cols-2 gap-3 md:grid-cols-4"
            >
              <div class="kpi-card kpi-blue">
                <p class="kpi-label">Total de menções</p>
                <p class="kpi-value">
                  {{ context.summary.totalMentions || 0 }}
                </p>
              </div>
              <div class="kpi-card kpi-green">
                <p class="kpi-label">% Positivo</p>
                <p class="kpi-value">{{ sentimentPct.positive }}%</p>
              </div>
              <div class="kpi-card kpi-rose">
                <p class="kpi-label">Engajamento</p>
                <p class="kpi-value">
                  {{ context.summary.engagement?.total || 0 }}
                </p>
              </div>
              <div
                class="kpi-card"
                :class="context.summary.trend >= 0 ? 'kpi-green' : 'kpi-rose'"
              >
                <p class="kpi-label">Tendência</p>
                <p class="kpi-value">
                  {{ context.summary.trend > 0 ? '+' : ''
                  }}{{ context.summary.trend || 0 }}%
                </p>
              </div>
            </section>

            <!-- Sentiment breakdown bar -->
            <section v-if="context.summary" class="doc-section">
              <h3 class="doc-h3">Distribuição de Sentimento</h3>
              <div
                class="mb-2 flex h-3 overflow-hidden rounded-full bg-slate-100"
              >
                <div
                  class="bg-emerald-500"
                  :style="{ width: `${sentimentPct.positive}%` }"
                />
                <div
                  class="bg-rose-500"
                  :style="{ width: `${sentimentPct.negative}%` }"
                />
                <div
                  class="bg-slate-400"
                  :style="{ width: `${sentimentPct.neutral}%` }"
                />
              </div>
              <div class="flex flex-wrap gap-4 text-xs">
                <span class="flex items-center gap-1.5"
                  ><span class="size-2 rounded-full bg-emerald-500" />Positivo:
                  {{ context.summary.sentiment?.positive || 0 }} ({{
                    sentimentPct.positive
                  }}%)</span
                >
                <span class="flex items-center gap-1.5"
                  ><span class="size-2 rounded-full bg-rose-500" />Negativo:
                  {{ context.summary.sentiment?.negative || 0 }} ({{
                    sentimentPct.negative
                  }}%)</span
                >
                <span class="flex items-center gap-1.5"
                  ><span class="size-2 rounded-full bg-slate-400" />Neutro:
                  {{ context.summary.sentiment?.neutral || 0 }} ({{
                    sentimentPct.neutral
                  }}%)</span
                >
              </div>
            </section>

            <!-- Insights highlights -->
            <section
              v-if="context.insights"
              class="grid grid-cols-1 gap-4 md:grid-cols-3"
            >
              <div class="insight-card insight-positive">
                <h3 class="insight-title">
                  <span class="i-lucide-thumbs-up size-4" />
                  Pontos Fortes
                </h3>
                <ul class="insight-list">
                  <li
                    v-for="(p, i) in context.insights.positivePoints"
                    :key="`sp-${i}`"
                  >
                    {{ p }}
                  </li>
                </ul>
              </div>
              <div class="insight-card insight-negative">
                <h3 class="insight-title">
                  <span class="i-lucide-triangle-alert size-4" />
                  Vulnerabilidades
                </h3>
                <ul class="insight-list">
                  <li
                    v-for="(p, i) in context.insights.negativePoints"
                    :key="`sn-${i}`"
                  >
                    {{ p }}
                  </li>
                </ul>
              </div>
              <div class="insight-card insight-info">
                <h3 class="insight-title">
                  <span class="i-lucide-lightbulb size-4" />
                  Recomendações
                </h3>
                <ul class="insight-list">
                  <li
                    v-for="(p, i) in context.insights.recommendations"
                    :key="`sr-${i}`"
                  >
                    {{ p }}
                  </li>
                </ul>
              </div>
            </section>

            <!-- Rendered markdown report -->
            <section class="doc-section">
              <div class="report-prose" v-html="renderedHtml" />
            </section>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* Chatwoot tokens — unprefixed RGB triplets wrapped via rgb() */
.report-modal-container {
  background: rgb(var(--surface-1));
  color: rgb(var(--slate-12));
}
.report-body {
  background: rgb(var(--background-color));
  color: rgb(var(--slate-12));
}

/* KPI cards */
.kpi-card {
  border-radius: 0.75rem;
  padding: 1rem;
  border: 1px solid rgb(var(--border-weak));
  background: rgb(var(--surface-1));
}
.kpi-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgb(var(--slate-11));
  margin: 0 0 0.5rem;
}
.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.1;
  color: rgb(var(--slate-12));
}
.kpi-blue .kpi-value {
  color: rgb(var(--blue-11));
}
.kpi-green .kpi-value {
  color: rgb(var(--teal-11));
}
.kpi-rose .kpi-value {
  color: rgb(var(--ruby-11));
}

/* Document section */
.doc-section {
  background: rgb(var(--surface-1));
  border: 1px solid rgb(var(--border-weak));
  border-radius: 0.75rem;
  padding: 1.25rem 1.5rem;
}
.doc-h3 {
  font-size: 0.9rem;
  font-weight: 700;
  color: rgb(var(--slate-12));
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 0.75rem;
}

/* Insight cards — semantic accents with subtle tinted backgrounds */
.insight-card {
  border-radius: 0.75rem;
  padding: 1rem;
  border: 1px solid rgb(var(--border-weak));
  background: rgb(var(--surface-1));
}
.insight-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
}
.insight-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.55;
  color: rgb(var(--slate-12));
}
.insight-list li {
  margin-bottom: 0.35rem;
  padding-left: 0.75rem;
  position: relative;
}
.insight-list li::before {
  content: '•';
  position: absolute;
  left: 0;
  font-weight: 700;
}
.insight-positive {
  border-left: 3px solid rgb(var(--teal-11));
}
.insight-positive .insight-title {
  color: rgb(var(--teal-11));
}
.insight-positive .insight-list li::before {
  color: rgb(var(--teal-11));
}
.insight-negative {
  border-left: 3px solid rgb(var(--ruby-11));
}
.insight-negative .insight-title {
  color: rgb(var(--ruby-11));
}
.insight-negative .insight-list li::before {
  color: rgb(var(--ruby-11));
}
.insight-info {
  border-left: 3px solid rgb(var(--blue-11));
}
.insight-info .insight-title {
  color: rgb(var(--blue-11));
}
.insight-info .insight-list li::before {
  color: rgb(var(--blue-11));
}

/* Rendered markdown — adapts to theme */
.report-prose {
  color: rgb(var(--slate-12));
}
.report-prose :deep(h1) {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgb(var(--slate-12));
  margin: 0.5rem 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgb(var(--border-weak));
}
.report-prose :deep(h2) {
  font-size: 1.25rem;
  font-weight: 700;
  color: rgb(var(--blue-11));
  margin: 1.5rem 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.report-prose :deep(h3) {
  font-size: 1.125rem;
  font-weight: 600;
  color: rgb(var(--slate-12));
  margin: 1.25rem 0 0.5rem;
}
.report-prose :deep(h4) {
  font-size: 1rem;
  font-weight: 600;
  color: rgb(var(--slate-12));
  margin: 1rem 0 0.5rem;
}
.report-prose :deep(p) {
  font-size: 0.9rem;
  line-height: 1.7;
  color: rgb(var(--slate-12));
  margin-bottom: 0.75rem;
}
.report-prose :deep(ul),
.report-prose :deep(ol) {
  margin: 0 0 0.75rem 1.5rem;
  font-size: 0.9rem;
  color: rgb(var(--slate-12));
}
.report-prose :deep(ul) {
  list-style: disc;
}
.report-prose :deep(ol) {
  list-style: decimal;
}
.report-prose :deep(li) {
  line-height: 1.7;
  margin-bottom: 0.25rem;
}
.report-prose :deep(strong) {
  font-weight: 700;
  color: rgb(var(--slate-12));
}
.report-prose :deep(em) {
  font-style: italic;
  color: rgb(var(--slate-11));
}
.report-prose :deep(blockquote) {
  margin: 0 0 0.75rem;
  padding: 0.75rem 1rem;
  border-left: 4px solid rgb(var(--blue-11));
  background: rgb(var(--solid-blue));
  font-size: 0.9rem;
  font-style: italic;
  color: rgb(var(--slate-12));
  border-radius: 0 0.375rem 0.375rem 0;
}
.report-prose :deep(code) {
  background: rgb(var(--border-weak));
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-family: ui-monospace, monospace;
  font-size: 0.8rem;
  color: rgb(var(--slate-12));
}
.report-prose :deep(pre) {
  background: rgb(var(--slate-12));
  color: rgb(var(--surface-1));
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
  overflow-x: auto;
  font-size: 0.78rem;
}
.report-prose :deep(pre code) {
  background: transparent;
  color: inherit;
  padding: 0;
}
.report-prose :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  border: 1px solid rgb(var(--border-weak));
  border-radius: 0.5rem;
  overflow: hidden;
}
.report-prose :deep(thead) {
  background: rgb(var(--blue-9));
}
.report-prose :deep(th) {
  padding: 0.625rem 0.875rem;
  text-align: left;
  font-weight: 700;
  color: #ffffff;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.report-prose :deep(td) {
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid rgb(var(--border-weak));
  color: rgb(var(--slate-12));
}
.report-prose :deep(tbody tr:nth-child(even)) {
  background: rgb(var(--background-color));
}
.report-prose :deep(hr) {
  margin: 1.5rem 0;
  border: 0;
  border-top: 1px solid rgb(var(--border-weak));
}
.report-prose :deep(a) {
  color: rgb(var(--blue-11));
  text-decoration: underline;
}
.report-prose :deep(a:hover) {
  opacity: 0.8;
}
</style>
